import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, FileText, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';

const ForumGuidelines = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="py-12 bg-[#F9F9F7]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Playfair Display, serif'}}>
            Forum Guidelines
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Important information about using the <span className="text-[#8B1818] font-semibold">Lex</span>DoRight Forum
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back to Forum */}
        <Link to="/forum" className="inline-flex items-center text-[#8B1818] hover:text-[#701313] mb-8">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Forum
        </Link>

        {/* Disclaimer Section */}
        <div id="disclaimer" className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 mb-8">
          <div className="flex items-center mb-6">
            <AlertTriangle className="h-8 w-8 text-amber-500 mr-3" />
            <h2 className="text-2xl font-bold text-slate-900" style={{fontFamily: 'Playfair Display, serif'}}>
              Disclaimer
            </h2>
          </div>
          <div className="prose prose-slate max-w-none space-y-4 text-slate-700">
            <p>
              All information in this forum is for general information and educational purposes only and does not constitute legal advice, legal opinion, or an official ruling on any case or matter. Using this forum does not create a lawyer-client or attorney-client relationship between you and any action officer, administrator, or <span className="text-[#8B1818] font-semibold">Lex</span>DoRight.
            </p>
            <p>
              The advice or opinions of any online action officer are informal and do not bind <span className="text-[#8B1818] font-semibold">Lex</span>DoRight or any institution. You should consult a licensed lawyer or the proper government agency for formal, binding legal advice or official interpretations of laws, rules, and regulations.
            </p>
            <p>
              <span className="text-[#8B1818] font-semibold">Lex</span>DoRight, its owners, developers, and administrators are not responsible or liable for any loss, damage, or consequence arising from your use of, or reliance on, any content posted in this forum.
            </p>
            <p>
              By accessing or posting in the <span className="text-[#8B1818] font-semibold">Lex</span>DoRight forum, you agree that the administrators may edit or delete your posts, and suspend or ban your account at any time and without prior notice, especially in cases of violation of the forum guidelines, community standards, or the platform's terms of use.
            </p>
          </div>
        </div>

        {/* Guidelines Section */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
          <div className="flex items-center mb-6">
            <FileText className="h-8 w-8 text-[#8B1818] mr-3" />
            <h2 className="text-2xl font-bold text-slate-900" style={{fontFamily: 'Playfair Display, serif'}}>
              Guidelines for Users
            </h2>
          </div>
          <div className="prose prose-slate max-w-none">
            <ol className="space-y-4 text-slate-700">
              <li>Before posting, please browse the Frequently Asked Questions (FAQs) or existing threads to check if your question or concern has already been asked or addressed, and review any related discussions.</li>
              <li>Official or binding responses to legal issues, matters, or cases are issued only by the competent courts, tribunals, or government offices, and not through this forum or the <span className="text-[#8B1818] font-semibold">Lex</span>DoRight platform.</li>
              <li>Requests for verification or update on the status of any case, investigation, or administrative/judicial proceeding are not allowed and will be deleted.</li>
              <li>Questions regarding examination schedules, application status, venue assignments, or similar operational details of any bar, civil service, licensure, or professional examination will be deleted; contact the proper agency through its official channels.</li>
              <li>This forum is organized by subject matter; please post your queries in the appropriate category to help maintain order and searchability.</li>
              <li>Post a descriptive topic title and provide a brief, clear summary of your concern or question so that action officers and other users can respond appropriately.</li>
              <li>No spam, advertising, promotional content, or irrelevant links are allowed; such posts will be deleted and the related account may be suspended or banned without prior warning.</li>
              <li>Do not post offensive, defamatory, obscene, discriminatory, or otherwise unlawful content, including images, links, or attachments; violation may result in removal of content and banning of your account without prior warning.</li>
              <li>If you see content or behavior that appears to violate these guidelines, please report it to the administrators using the reporting tools or contact channels provided in the platform.</li>
              <li>Be respectful and courteous to administrators, action officers, and fellow users at all times; harassment, threats, or abusive language are strictly prohibited.</li>
              <li>Avoid text-message abbreviations or similar shorthand that makes your message difficult to understand; if your post is unclear, the administrator or action officer may ask you to rewrite or clarify your message.</li>
            </ol>
            <div className="mt-8 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <p className="font-semibold text-slate-900">
                You acknowledge that by using <span className="text-[#8B1818] font-semibold">Lex</span>DoRight and participating in this forum, you accept and agree to be bound by this Disclaimer and the Guidelines for Users, as may be amended from time to time.
              </p>
            </div>
          </div>
        </div>

        {/* Back to Forum Button */}
        <div className="mt-8 text-center">
          <Link to="/forum">
            <Button className="bg-[#8B1818] text-white hover:bg-[#701313]">
              Return to Forum
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForumGuidelines;
